// Notes Database abstraction for IndexedDB
// Utilizing dexie.js - a wrapper around IndexedDB
import {
  Note,
  TypeOfNote,
  UUID,
  Profile,
  RelatedResult,
  isUUID,
} from '@annote/types';

import { ServerDB, ClientDB, Metadata } from './types';
import { NotesDB } from './notesDb';
// import { SearchModule } from './search';
import { DexieSyncService } from './sync/dexieSync';

const WEIGHTED_SEARCH_BY_TYPE: Record<TypeOfNote, number> = {
  takeaway: 5,
  source: 1,
  annote: 3,
};

// TODO list for dexiedb
// - remove deletedAt notes from the db and don't index on it
// - make links and backlinks of type set
// - maybe use "search" for getNotesByTitle and then not index by title

// helper function to ensure links and backlinks aren't duplicated
const removeDuplicates = (array: string[]): string[] => {
  return [...new Set(array)];
};

export class DexieDatabase implements ClientDB {
  db: NotesDB;

  serverDB?: ServerDB;

  // searchModule?: SearchModule;

  userId?: UUID;

  private syncService?: DexieSyncService;

  // cached profile for isUrlBlocked
  // updated whenever getProfile is called
  profile?: Profile;

  private _onlineSearchNotes?: (query: string, types?: TypeOfNote[], limit?: number) => Promise<Note[]>;

  constructor(serverDB?: ServerDB) {
    this.db = new NotesDB();
    this.serverDB = serverDB;
  }

  // addSearchModule = (searchModule: SearchModule): void => {
  //   this.searchModule = searchModule;
  // };

  // The profile info needed should be inside the DB so initialize it from there
  // cache the userId and isURLBlocked function
  init = async (): Promise<void> => {
    if (this.userId) return; // already initialized
    console.log('dexieDB - starting init');
    // will set this.profile
    await this.getProfile();

    if (!this.serverDB) {
      throw new Error('dexieDB init: no server DB');
    }

    this.userId = this.profile?.userId;

    // TODO! - check if serverDB.userId is the same as profileId

    // If online, use the serverDB fetch and searchNotes
    this.fetch = this.serverDB.fetch.bind(this.serverDB);
    this._onlineSearchNotes = this.serverDB.searchNotes.bind(this.serverDB);
    this.syncService = new DexieSyncService(this);
  };

  sync = async (): Promise<void> => {
    if (!this.syncService) throw new Error('DexieDB Sync - no syncService setup yet');
    return this.syncService.syncWithServer();
  };

  private isUrlBlocked = (url: string): boolean => {
    const { host } = new URL(url);
    if (!this.profile) throw new Error('dexieDB isUrlBlocked - called but profile not initialized');
    return this.profile.blockedHosts.includes(host);
  };

  updateLastSyncTime = async (syncTime: string = new Date().toISOString()): Promise<void> => {
    await this.db.metadata.put({ key: 'lastSync', value: syncTime });
  };

  getLastSyncTime = async (): Promise<string> => {
    const lastSync = await this.db.metadata.get('lastSync');
    return lastSync ? lastSync.value : '2023-01-01T00:00:00.000Z';
  };

  getNotes = async (ids?: UUID[]): Promise<Note[]> => {
    if (ids && ids.length > 0) {
      // Filter out any invalid IDs before querying
      const validIds = ids.filter((id) => isUUID(id));
      if (validIds.length < ids.length) {
        console.warn('dexieDB getNotes: some invalid ids were provided', ids);
      }
      if (validIds.length === 0) return [];
      return this.db.notes.where('id').anyOf(validIds).toArray();
    }
    // If no ids provided, return all notes
    return this.db.notes.toArray();
  };

  getNotesByUrl = async (url: string): Promise<{ notes: Note[]; isBlocked: boolean }> => {
    if (this.isUrlBlocked && this.isUrlBlocked(url)) return Promise.resolve({ notes: [], isBlocked: true });
    // TODO filter out deleted at notes
    const notes = await this.db.notes.where({ url }).toArray();
    return { notes, isBlocked: false };
  };

  getNoteById = async (id: UUID): Promise<Note | null> => {
    const n = await this.db.notes.get(id);
    if (!n) return null;
    n.backlinks = n.backlinks || []; // weird dexie issue
    n.links = n.links || [];
    return n;
  };

  // TODO - this might be better implemented with search if available
  // Then we don't have to index on title either
  getNoteByTitle = async (title: string): Promise<Note | null> => {
    const note = await this.db.notes.filter((n) => n?.title.toLowerCase() === title?.toLowerCase()).first();
    return note || null;
  };

  // When there is no search query - fill with the latest notes
  _getLatestNotes = (limit: number = 100, types: TypeOfNote[] = ['takeaway', 'source']): Promise<Note[]> => {
    console.log('dexieDB getting latest notes', types, limit);
    return this.db.notes
      .orderBy('syncedAt')
      .reverse() // Reverse the order (descending)
      .filter((n) => types.includes(n.type) && !n.deletedAt)
      .limit(limit)
      .toArray();
  };

  // Note - Without this scoring results are 18ms and with they are 300ms
  // These results and speeds honestly aren't bad.  May not need searchModule
  private _basicSearch = async (query: string, types?: TypeOfNote[], limit: number = 10): Promise<Note[]> => {
    console.log('WARNING! dexieDB basic search', query, types, limit);
    const lowercaseQuery = query.toLowerCase();

    return this.db.notes
      .filter((note) => {
        const matchesType = !types || types.includes(note.type);
        const matchesQuery =
          note.title.toLowerCase().includes(lowercaseQuery) || note.value.toLowerCase().includes(lowercaseQuery);
        return matchesType && matchesQuery && !note.deletedAt;
      })
      .toArray()
      .then((notes) => {
        // Calculate score for each note
        const scoredNotes = notes.map((note) => {
          const titleMatches = (note.title.toLowerCase().match(new RegExp(lowercaseQuery, 'g')) || []).length;
          const valueMatches = (note.value.toLowerCase().match(new RegExp(lowercaseQuery, 'g')) || []).length;
          const score =
            ((titleMatches / note.title.length) * 4 + valueMatches / note.value.length) *
            WEIGHTED_SEARCH_BY_TYPE[note.type];
          return { note, score };
        });

        // Sort by score (descending) and return the top 'limit' results
        return scoredNotes
          .sort((a, b) => b.score - a.score)
          .slice(0, limit)
          .map((item) => item.note);
      });
  };

  _offlineSearchNotes = async (query: string, types?: TypeOfNote[], limit: number = 10): Promise<Note[]> => {
    console.log('dexieDB searching notes', query, types, limit, 'db', this);
    const startTime = performance.now();

    if (query.length === 0) {
      const latestNotes = await this._getLatestNotes(limit, types);
      const endTime = performance.now();
      console.log(`Dexie: empty search took ${endTime - startTime} ms`);
      return latestNotes;
    }

    // if (this.searchModule) {
    //   const results = await this.searchModule.searchNotes(query, types, limit);
    //   const endTime = performance.now();
    //   console.log(`Dexie: Search for '${query}' took ${endTime - startTime} ms`);
    //   return results;
    // }

    const results = await this._basicSearch(query, types, limit);
    const endTime = performance.now();
    console.log(`Dexie: Basic search for '${query}' took ${endTime - startTime} ms`);
    return results;
  };

  searchNotes = async (query: string, types?: TypeOfNote[], limit: number = 10): Promise<Note[]> => {
    // TODO - navigator.onLine isn't the best indicator of online status
    if (query.length > 0 && navigator.onLine && this._onlineSearchNotes) {
      // for a query when you're online, use supabase's search
      return this._onlineSearchNotes(query, types, limit);
    }
    // Use offline search if online search is not available
    return this._offlineSearchNotes(query, types, limit);
  };

  // WRITE OPERATIONS

  updateNotes = async (notes: Note[]): Promise<void> => {
    console.log('dexiedb updating notes', notes.length, notes);
    // set syncedAt to null to indicate that the note is not synced
    const updatedNotes = notes
      .filter((n) => !n.suggestionId) // don't sync suggestions
      .map((note: Note) => ({
        ...note,
        syncedAt: null as unknown as Note['syncedAt'],
        // Ensure links and backlinks aren't duplicated
        links: removeDuplicates(note.links),
        backlinks: removeDuplicates(note.backlinks),
      }));

    // Update the search index if there is one
    // if (this.searchModule) this.searchModule.updateNotes(updatedNotes);

    // new changes will overwrite any existing changes
    await this.db.changesToNotes.bulkPut(updatedNotes);
    await this.db.notes.bulkPut(updatedNotes);
    if (this.syncService) this.syncService.debouncedSyncWithServer();
  };

  clear = async (): Promise<void> => {
    console.log('dexiedb clearing db');
    await this.db.notes.clear();
    await this.db.changesToNotes.clear();
    await this.db.metadata.clear();
    // this.searchModule = undefined;
  };

  // Logical delete Note by setting the deletedAt field
  deleteNotes = async (notes: Note[]): Promise<void> => {
    const now = new Date().toISOString();
    const deletedNotes = notes
      .filter((n) => !n.suggestionId) // ignore suggestions
      .map((note) => ({ ...note, deletedAt: now }));
    console.log('dexiedb deleting notes', deletedNotes.length, deletedNotes);
    // if (this.searchModule) await this.searchModule.deleteNotes(notes);

    // Remove all relevant denormalized Links, Backlinks and createdFromIds
    const deletedIds = deletedNotes.map((dn) => dn.id);
    const idsToUpdate: UUID[] = [];

    deletedNotes.forEach((dn) => {
      // Ensure links and backlinks are arrays before spreading
      if (Array.isArray(dn.links)) {
        idsToUpdate.push(...dn.links);
      }
      if (Array.isArray(dn.backlinks)) {
        idsToUpdate.push(...dn.backlinks);
      }
      if (dn.createdFromId) {
        idsToUpdate.push(dn.createdFromId);
      }
    });

    const allIdsToUpdate: UUID[] = Array.from(new Set(idsToUpdate)).filter((id) => !deletedIds.includes(id));
    console.log('dexiedb allIdsToUpdate', allIdsToUpdate.length, allIdsToUpdate);

    // Only fetch and update related notes if there are IDs to update
    if (allIdsToUpdate.length > 0) {
      const notesToUpdate = await this.getNotes(allIdsToUpdate);
      console.log('dexiedb notesToUpdate', notesToUpdate.length, notesToUpdate);

      const updatedNotes = notesToUpdate.map((n) => {
        const links = (n.links || []).filter((id) => !deletedIds.includes(id));
        const backlinks = (n.backlinks || []).filter((id) => !deletedIds.includes(id));
        const createdFromId = n.createdFromId && !deletedIds.includes(n.id) ? n.createdFromId : undefined;
        return { ...n, links, backlinks, createdFromId };
      });

      console.log('dexiedb updatedNotes', updatedNotes.length, updatedNotes);
      // Remove all links to the deleted note before marking it deleted
      await this.updateNotes(updatedNotes);
    }

    await this.db.changesToNotes.bulkPut(deletedNotes);
    await this.db.notes.bulkDelete(deletedIds);
    if (this.syncService) this.syncService.debouncedSyncWithServer();
  };

  getProfile = async (forceRefresh: boolean = false): Promise<Profile | null> => {
    // If not forcing refresh and we have a cached profile, return it
    if (!forceRefresh && this.profile) {
      return this.profile;
    }

    // Check first for the profile in the db
    this.profile = ((await this.getMetadata('profile')) as Profile) || undefined;
    if (!this.profile) {
      // Dexie probably isn't loaded yet - try to get it directly from the serverDB
      if (this.serverDB) {
        console.log('dexieDB - getting profile from serverDB');
        this.profile = (await this.serverDB.getProfile(forceRefresh)) || undefined;
      }
    }
    if (!this.profile) {
      console.warn('dexieDB getProfile: no profile found in dexieDB or serverDB - database has no data');
      return null;
    }
    return this.profile;
  };

  updateProfile = async (profile: Profile): Promise<void> => {
    const updatedProfile = { ...profile, syncedAt: null };
    await this.setMetadata('profile', updatedProfile);
    this.profile = updatedProfile;
    if (this.syncService) this.syncService.debouncedSyncWithServer();
  };

  findRelatedNotes = async (
    ids: UUID[],
    limit: number = 10,
    similarityThreshold: number = 0.7,
  ): Promise<RelatedResult[]> => {
    if (this.serverDB) {
      return this.serverDB.findRelatedNotes(ids, limit, similarityThreshold);
    }
    throw new Error('DexieDB findRelatedNotes - no online server to find related notes from');
  };

  isAuthenticated = async (): Promise<boolean> => {
    // For DexieDatabase, check if we have a serverDB and if it's authenticated
    if (this.serverDB) {
      return this.serverDB.isAuthenticated();
    }
    return false;
  };

  getMetadata = async (key: string): Promise<Metadata | null> => {
    const result = await this.db.metadata.get(key);
    return result ? (JSON.parse(result.value) as Metadata) : null;
  };

  setMetadata = async (key: string, value: Metadata): Promise<void> => {
    await this.db.metadata.put({ key, value: JSON.stringify(value) });
    const savedAt = new Date().toISOString();
    await this.db.metadata.put({ key: `${key}-SavedAt`, value: savedAt });
  };

  // TODO - is this needed anymore?  Was used for profile syncing
  getMetadataSavedAt = async (key: string): Promise<string | null> => {
    const result = await this.db.metadata.get(`${key}-SavedAt`);
    return result ? result.value : null;
  };

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
  fetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    // You need supabase to do the function fetches.  In sync.ts we override this with supabase fetch
    // If it hasn't been overridden, the user is offline or syncing is not working.
    throw new Error('DexieDB fetch issue - supabase fetch should be overridding this');
  };

  // Get annote index for skip-over optimization
  getAnnoteIndex = async (): Promise<Record<UUID, { links: UUID[]; backlinks: UUID[] }>> => {
    try {
      // Query all annote notes from the database
      const annotes = await this.db.notes
        .where('type')
        .equals('annote')
        .and((note) => !note.deletedAt)
        .toArray();

      const index: Record<UUID, { links: UUID[]; backlinks: UUID[] }> = {};

      annotes.forEach((annote) => {
        index[annote.id] = {
          links: Array.from(new Set(annote.links || [])),
          backlinks: Array.from(new Set(annote.backlinks || [])),
        };
      });

      return index;
    } catch (error) {
      console.error('DexieDB getAnnoteIndex error:', error);
      return {};
    }
  };
}
